import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton, Divider, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, Tooltip, Zoom, CircularProgress, Skeleton,
  Grid, InputAdornment, List, ListItem, ListItemText, ListItemIcon, Switch, FormControlLabel, GlobalStyles
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Close as CloseIcon, MoreVert as MoreVertIcon, 
  Warning as WarningIcon, DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { campaignApi, accrualApi } from '../services/api';
import { 
  formatCurrency,
  generateMonthsRange,
  determineSnapshotCampaignStatus,
  calculateUnused,
  calculateActualGrossCost,
  calculateSavingBasedOnStatus,
  calculateCheck,
  calculateSavingPercentage
} from '../utils/calculations';

// Excel View Wrapper - Isolated CSS approach similar to ExcelLikeView
const ExcelViewWrapper = ({ children }) => {
  useEffect(() => {
    // Create a style element to support table-box scrolling
    const styleElement = document.createElement('style');
    styleElement.id = 'excel-snapshot-view-override-styles';
    styleElement.innerHTML = `
      /* CRITICAL: Support table-box container for Excel Snapshot View */
      .excel-snapshot-view-isolated {
        overflow-x: hidden !important;
        overflow-y: visible !important;
        max-width: 100% !important;
        width: 100% !important;
        position: relative !important;
      }
      
      /* Override any container constraints within excel snapshot view */
      .excel-snapshot-view-isolated .MuiContainer-root,
      .excel-snapshot-view-isolated .MuiBox-root {
        max-width: none !important;
        width: auto !important;
        overflow-x: visible !important;
      }
      
      /* Support table-box scrolling */
      .excel-snapshot-view-isolated .table-box {
        overflow: hidden !important;
        position: relative !important;
        max-width: 81vw !important; /* Adjusted to 73vw as requested */
        width: 81vw !important;
      }
      
      .excel-snapshot-view-isolated .table-box .MuiTableContainer-root {
        overflow-x: auto !important;
        overflow-y: auto !important;
        max-width: 100% !important;
        width: 100% !important;
        height: 100% !important;
      }
      
      /* Custom scrollbar for table container */
      .excel-snapshot-view-isolated .table-box .MuiTableContainer-root::-webkit-scrollbar {
        width: 12px !important;
        height: 12px !important;
      }
      
      .excel-snapshot-view-isolated .table-box .MuiTableContainer-root::-webkit-scrollbar-track {
        background: #fff3e0 !important;
        border-radius: 6px !important;
      }
      
      .excel-snapshot-view-isolated .table-box .MuiTableContainer-root::-webkit-scrollbar-thumb {
        background: #ff9800 !important;
        border-radius: 6px !important;
        border: 2px solid #fff3e0 !important;
      }
      
      .excel-snapshot-view-isolated .table-box .MuiTableContainer-root::-webkit-scrollbar-thumb:hover {
        background: #f57c00 !important;
      }
      
      /* Force table to expand to trigger scroll */
      .excel-snapshot-view-isolated .table-box .MuiTable-root {
        width: auto !important;
        min-width: max-content !important;
        max-width: none !important;
        table-layout: auto !important;
      }
      
      /* Allow cells to size naturally but enable resize */
      .excel-snapshot-view-isolated .table-box .MuiTableCell-root {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        position: relative !important;
      }
      
      /* Resize handle styles */
      .excel-snapshot-view-isolated .resize-handle {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        cursor: col-resize;
        background-color: rgba(255, 152, 0, 0.5);
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 101;
      }
      
      .excel-snapshot-view-isolated .resize-handle:hover {
        background-color: #ff9800;
        opacity: 1;
      }
      
      .excel-snapshot-view-isolated .MuiTableCell-root:hover .resize-handle {
        opacity: 1;
      }
      
      /* Sticky header for table-box */
      .excel-snapshot-view-isolated .table-box .MuiTableCell-head {
        position: sticky !important;
        top: 0 !important;
        z-index: 100 !important;
        background-color: #fff3e0 !important;
        border-bottom: 2px solid rgba(255, 152, 0, 0.4) !important;
      }
      
      /* Resizing state styles */
      .excel-snapshot-view-isolated.resizing {
        user-select: none !important;
        pointer-events: auto !important;
      }
      
      .excel-snapshot-view-isolated.resizing * {
        user-select: none !important;
      }
    `;
    
    document.head.appendChild(styleElement);
    
    return () => {
      const existingStyle = document.getElementById('excel-snapshot-view-override-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
  
  return (
    <div className="excel-snapshot-view-isolated" style={{
      width: '100%',
      overflowX: 'hidden',
      overflowY: 'visible',
      maxWidth: '100%',
      position: 'relative'
    }}>
      {children}
    </div>
  );
};

// Define consistent font sizes
const FONT_SIZES = {
  header: '0.9rem',
  subtitle: '0.75rem', 
  body: '0.75rem',
  caption: '0.7rem',
  small: '0.65rem',
  table: '0.7rem',
  tableHeader: '0.7rem',
  number: '0.7rem'
};

/**
 * CampaignsSnapshotExcel Component
 * 
 * Specialized Excel-like view for snapshot data - read-only display optimized for historical data
 * URL: /excel-view/{campaignId}/{mediaType}?snapshotMonth=YYYY-MM
 */
const CampaignsSnapshotExcel = () => {
  // URL Parameters and Navigation
  const { campaignId, mediaType } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const snapshotMonth = params.get('snapshotMonth');
  const navigate = useNavigate();
  
  // Core Data States
  const [campaign, setCampaign] = useState(null);
  const [accrualData, setAccrualData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Show snackbar message
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [monthRange, setMonthRange] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [showCampaignSelector, setShowCampaignSelector] = useState(false);

  // Column widths for Excel-like display with resizable columns
  const [columnWidths, setColumnWidths] = useState({
    month: 90,
    status: 80,
    media: 70,
    sub_media: 110,
    vendor: 120,
    format: 120,
    region: 110,
    ooh: 90,
    asset: 140,
    given: 110,
    planning: 120,
    actual: 120,
    net_cost: 110,
    tracking: 110,
    tax: 100,
    coupon: 110,
    ooscope: 110,
    saving: 140,
    po: 100,
    unused: 120,
    ntw: 100,
    north: 100,
    central: 100,
    rest: 100,
    ghcm: 100,
    mekong: 100,
    check: 120
  });

  // Column resize state - Match ExcelLikeView exactly
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Column resize handlers - Exact copy from ExcelLikeView
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  };

  const handleMouseMove = (e) => {
    if (!isResizing || !resizingColumn) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    setResizingColumn(null);
    setStartX(0);
    setStartWidth(0);
  };

  // Add event listeners for mouse move and up - Exact copy from ExcelLikeView
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Add resizing class to container
      const container = document.querySelector('.excel-snapshot-view-isolated');
      if (container) {
        container.classList.add('resizing');
      }
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Remove resizing class from container
        if (container) {
          container.classList.remove('resizing');
        }
      };
    }
  }, [isResizing, resizingColumn, startX, startWidth]);

  // Process and normalize accrual data for display
  const processAccrualDataWithCampaign = (data, campaignData) => {
    if (!Array.isArray(data)) return [];
    
    const currencyFields = [
      'given', 'planning_gross_cost', 'net_net_cost', 
      'whtax', 'ooscope_fees', 'tracking_fees', 
      'cost_on_coupon_voucher_etc', 'po_cost', 'cost_on_ecpms',
      'ntw', 'north', 'central_mid', 'the_rest_of_central', 'ghcm', 'mekong', 'coupon'
    ];

    return data.map(row => {
      const newRow = { ...row };
      currencyFields.forEach(field => {
        if (newRow.hasOwnProperty(field)) {
          if (typeof newRow[field] === 'string') {
            newRow[field] = parseFloat(newRow[field].replace(/,/g, '')) || 0;
          } else if (typeof newRow[field] !== 'number') {
            newRow[field] = 0;
          }
        }
      });
      
      // Calculate actual_gross_cost automatically from components
      newRow.actual_gross_cost = calculateActualGrossCost(newRow);
      
      // Calculate unused
      newRow.unused = calculateUnused(newRow);
      
      // Calculate saving based on snapshot campaign status (only show for DONE campaigns)
      newRow.saving = calculateSavingBasedOnStatus(newRow, campaignData, newRow.actual_gross_cost, false, snapshotMonth);
      
      // Calculate saving percentage
      newRow.savingPercentage = calculateSavingPercentage(newRow, campaignData, newRow.actual_gross_cost, false, snapshotMonth);
      
      // Calculate check value
      newRow.check = calculateCheck(newRow);
      
      return newRow;
    });
  };

  /**
   * Fetch snapshot data for Excel view
   */
  const fetchSnapshotData = async () => {
    console.log('CampaignsSnapshotExcel: fetchSnapshotData called with:', { campaignId, mediaType, snapshotMonth });
    setLoading(true);
    
    try {
      // Get snapshot campaign info
      const campaignRes = await campaignApi.getCampaignsBySnapshot(snapshotMonth);
      console.log('Snapshot campaign response:', campaignRes.data);
      
      // Find current campaign in snapshot data - handle different response formats
      let campaignData = null;
      let campaignsList = [];
      
      if (campaignRes.data.results) {
        campaignsList = campaignRes.data.results;
      } else if (campaignRes.data.snapshot?.campaigns) {
        campaignsList = campaignRes.data.snapshot.campaigns;
      } else if (Array.isArray(campaignRes.data)) {
        campaignsList = campaignRes.data;
      }
      
      campaignData = campaignsList.find(
        item => item.id.toString() === campaignId.toString()
      );
      
      if (!campaignData) {
        console.warn('Campaign not found in snapshot');
        await checkCampaignAvailability();
        return;
      }
      
      // Set campaign basic info from snapshot - handle new API format
      const processedCampaignData = {
        ...campaignData,
        campaign_name: campaignData.Campaign_Name || campaignData.campaign_name || 'Unknown Campaign',
        brand_name: campaignData.Brand || campaignData.brand_name || campaignData.brand || 'Unknown Brand',
        sub_brand_name: campaignData.Sub_Brand || campaignData.sub_brand_name || campaignData.sub_brand,
        start_date: campaignData.Start_Date || campaignData.start_date || campaignData.start_date_actual,
        end_date: campaignData.End_Date || campaignData.end_date || campaignData.end_date_actual,
        campaign_type: campaignData.Campaign_Type || campaignData.campaign_type,
        objective: campaignData.Objective_KPI || campaignData.objective
      };
      setCampaign(processedCampaignData);
      
      // Generate month range for Excel columns
      if (campaignData.start_date && campaignData.end_date) {
        const months = generateMonthsRange(
          campaignData.start_date,
          campaignData.end_date
        );
        setMonthRange(months);
      }
      
      // Fetch detailed accrual data for Excel view
      console.log('[SNAPSHOT EXCEL] Fetching accrual data');
      const snapshotAccrualResponse = await accrualApi.getSnapshotAccrualData(
        campaignId, 
        snapshotMonth, 
        mediaType
      );
      
      console.log('[SNAPSHOT EXCEL] Accrual data response:', snapshotAccrualResponse.data);
      
      // Process the data for Excel display
      const backendAccrualData = snapshotAccrualResponse.data || [];
      const finalProcessedSnapshotData = processAccrualDataWithCampaign(backendAccrualData, processedCampaignData);
      
      setAccrualData(finalProcessedSnapshotData);
      setError(null);
      
    } catch (err) {
      console.error('Error fetching snapshot data:', err);
      
      if (err.response?.status === 404) {
        setError(`Campaign không tồn tại trong snapshot tháng ${snapshotMonth}.`);
        await checkCampaignAvailability();
      } else {
        setError('Không thể tải dữ liệu snapshot. Vui lòng thử lại sau.');
      }
      
      setAccrualData([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if campaign exists in snapshot data and provide alternatives
  const checkCampaignAvailability = async () => {
    try {
      const snapshotResponse = await campaignApi.getCampaignsSnapshot(snapshotMonth);
      
      // Handle different response formats
      let availableCampaigns = [];
      if (snapshotResponse.results) {
        availableCampaigns = snapshotResponse.results;
      } else if (snapshotResponse.snapshot?.campaigns) {
        availableCampaigns = snapshotResponse.snapshot.campaigns;
      } else if (Array.isArray(snapshotResponse.data)) {
        availableCampaigns = snapshotResponse.data;
      } else if (Array.isArray(snapshotResponse)) {
        availableCampaigns = snapshotResponse;
      }
      
      const currentCampaignExists = availableCampaigns.some(
        campaign => String(campaign.id) === String(campaignId)
      );
      
      if (!currentCampaignExists && availableCampaigns.length > 0) {
        showSnackbar(
          `Campaign không tồn tại trong snapshot tháng ${snapshotMonth}. ` +
          `Có ${availableCampaigns.length} campaigns khác có sẵn.`
        );
        
        // Process available campaigns for display
        const processedCampaigns = availableCampaigns.map(campaign => ({
          ...campaign,
          campaign_name: campaign.Campaign_Name || campaign.campaign_name || 'Unknown Campaign',
          brand_name: campaign.Brand || campaign.brand_name || campaign.brand || 'Unknown Brand',
          given_lc: campaign.Given_Cost_Excl_VAT_VND || campaign.given_lc || 0
        }));
        
        setAvailableCampaigns(processedCampaigns);
        setShowCampaignSelector(true);
      }
    } catch (error) {
      console.error('Error checking campaign availability:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (campaignId && snapshotMonth) {
      fetchSnapshotData();
    } else {
      setLoading(false);
      setError('Missing required parameters: campaignId or snapshotMonth');
    }
  }, [campaignId, mediaType, snapshotMonth]);

  // Helper function to determine which columns to show based on media type
  const shouldShowColumn = (columnName) => {
    const mediaTypeLower = (mediaType || '').toLowerCase();
    
    switch (columnName) {
      case 'digital_vendor':
      case 'digital_ad_format':
        return mediaTypeLower === 'digital' || mediaTypeLower === 'digital amplify';
      case 'ooh_format':
        return mediaTypeLower === 'ooh';
      case 'asset_video_length_image_type':
        return mediaTypeLower === 'digital' || mediaTypeLower === 'digital amplify';
      default:
        return true;
    }
  };

  // Helper function to render table body cells with consistent widths - Match ExcelLikeView
  const renderTableBodyCell = (columnKey, children, align = 'left', sx = {}) => {
    const width = columnWidths[columnKey];
    return (
      <TableCell 
        sx={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          fontSize: FONT_SIZES.table,
          borderRight: '1px solid rgba(224, 224, 224, 0.5)',
          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
          py: { xs: 0.5, sm: 0.75 },
          px: { xs: 0.5, sm: 0.75 },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...sx
        }}
        align={align}
      >
        {children}
      </TableCell>
    );
  };

  // Helper function to clean sub_media_type for display
  const cleanSubMediaTypeForDisplay = (subMediaType) => {
    if (!subMediaType) return subMediaType;
    return subMediaType.replace(/_\d+$/, '');
  };

  // Render resizable header cell - Match ExcelLikeView exactly
  const renderResizableHeaderCell = (columnKey, children, align = 'center') => {
    const width = columnWidths[columnKey];
    return (
      <TableCell 
        sx={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          position: 'relative',
          padding: '8px 4px',
          // Sticky header styling  
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: '#fff3e0 !important',
          borderBottom: '2px solid rgba(255, 152, 0, 0.4) !important'
        }}
        align={align}
      >
        {children}
        <Box
          className="column-resizer"
          onMouseDown={(e) => handleMouseDown(e, columnKey)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '5px',
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.5)',
              width: '6px', // Slightly wider on hover
            },
            // Add a subtle line to indicate resizable area
            '&::after': {
              content: '""',
              position: 'absolute',
              right: '2px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '1px',
              height: '60%',
              backgroundColor: 'rgba(255, 152, 0, 0.3)',
              transition: 'all 0.2s ease',
            },
            '&:hover::after': {
              backgroundColor: 'rgba(255, 152, 0, 0.8)',
              width: '2px',
            }
          }}
        />
      </TableCell>
    );
  };

  return (
    <ExcelViewWrapper>
      <Box sx={{ 
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafafa',
        position: 'relative'
      }}>
      <Container maxWidth={false} sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1, sm: 1.5, md: 2 },
        py: { xs: 0.5, sm: 1 },
        width: '100%',
        maxWidth: 'none',
        height: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        margin: 0
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
        {/* Header for Snapshot Excel View */}
        <Paper elevation={0} sx={{ 
          flexShrink: 0,
          mb: 1,
          p: { xs: 1.5, sm: 2 },
          background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 50%, #ffe082 100%)',
          color: '#e65100',
          position: 'relative',
          overflow: 'hidden',
          border: '2px solid #ff9800',
          borderRadius: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 152, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 0
          }
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              maxWidth: '100%',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 2 }, 
                flex: 1, 
                minWidth: 0
              }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/campaigns?snapshotMonth=${snapshotMonth}`)}
                  sx={{ 
                    color: '#856404',
                    borderColor: 'rgba(133, 100, 4, 0.3)',
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 0.8 },
                    '&:hover': {
                      borderColor: '#856404',
                      backgroundColor: 'rgba(133, 100, 4, 0.1)'
                    }
                  }}
                >
                  Back to Snapshot
                </Button>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight="600" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                    📊 Snapshot Excel View
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    Historical data from {snapshotMonth} - Read-only Excel format
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label="📅 SNAPSHOT DATA" 
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)', 
                        color: '#856404',
                        fontWeight: 'bold',
                        fontSize: '0.7rem'
                      }} 
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '300px',
            flexDirection: 'column',
            gap: 2 
          }}>
            <CircularProgress size={60} sx={{ color: '#ff9800' }} />
            <Typography variant="body1" color="textSecondary">
              Loading snapshot data for {snapshotMonth}...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Campaign Info */}
            {campaign && (
              <Paper sx={{ 
                flexShrink: 0,
                p: { xs: 1, sm: 1.5 }, 
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#fff8e1',
                border: '1px solid rgba(255, 152, 0, 0.3)'
              }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="#e65100" fontWeight="bold">
                      Campaign
                    </Typography>
                    <Typography variant="body1" color="#bf360c">
                      {campaign.campaign_name || campaign.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="#e65100" fontWeight="bold">
                      Brand / Sub-brand
                    </Typography>
                    <Typography variant="body1" color="#bf360c">
                      {campaign.brand_name}
                      {campaign.sub_brand_name && ` / ${campaign.sub_brand_name}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="#e65100" fontWeight="bold">
                      Period
                    </Typography>
                    <Typography variant="body1" color="#bf360c">
                      {campaign.start_date && campaign.end_date
                        ? `${format(new Date(campaign.start_date), 'dd/MM/yyyy')} - ${format(new Date(campaign.end_date), 'dd/MM/yyyy')}`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="#e65100" fontWeight="bold">
                      Snapshot Status
                    </Typography>
                    <Chip
                      label={determineSnapshotCampaignStatus(campaign, snapshotMonth)}
                      sx={{
                        backgroundColor: 
                          determineSnapshotCampaignStatus(campaign, snapshotMonth) === 'DONE'
                            ? '#9e9e9e'
                            : determineSnapshotCampaignStatus(campaign, snapshotMonth) === 'ON-GOING'
                            ? '#4caf50'
                            : '#ffc107',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Excel-like Data Table Container - Fixed Width Box */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              width: '100%',
              overflow: 'hidden',
              border: '2px solid #ff9800',
              borderRadius: 2,
              backgroundColor: 'white',
              boxShadow: '0 4px 20px rgba(255, 152, 0, 0.15)'
            }}>
              {/* Table Header */}
              <Box sx={{ 
                flexShrink: 0,
                p: { xs: 1, sm: 1.5 },
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffecb3 100%)',
                borderBottom: '2px solid #ff9800'
              }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    color: '#e65100',
                    mb: 0.5
                  }}>
                    📸 Snapshot Excel Data - {snapshotMonth}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    color: '#f57700'
                  }}>
                    📅 Historical data in Excel format - Horizontal scroll enabled
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="#e65100" fontWeight="bold">
                    📋 EXCEL VIEW
                  </Typography>
                </Box>
              </Box>
              
              {/* Use table-box pattern like ExcelLikeView */}
              <div className="table-box">
                <TableContainer component={Paper} sx={{ 
                  maxHeight: '100%', 
                  overflow: 'auto',
                  width: '100%',
                  height: '100%',
                  borderRadius: '0px',
                  border: 'none',
                  boxShadow: 'none'
                }}>
                <Table 
                  className="excel-table"
                  size="small" 
                  stickyHeader 
                  sx={{ 
                    backgroundColor: 'white',
                    width: 'auto',
                    minWidth: 'max-content',
                    tableLayout: 'auto',
                  '& .MuiTableCell-root': {
                    fontSize: FONT_SIZES.table,
                    borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                    py: { xs: 0.5, sm: 0.75 },
                    px: { xs: 0.5, sm: 0.75 }, // More compact padding
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  },
                  '& .MuiTableCell-head': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#fff3e0 !important',
                    borderBottom: '2px solid rgba(255, 152, 0, 0.4)',
                    fontWeight: 700
                  }
                }}>
                  <TableHead>
                    <TableRow sx={{ 
                      '& .MuiTableCell-head': {
                        color: '#e65100',
                        fontWeight: 700,
                        fontSize: FONT_SIZES.tableHeader,
                        backgroundColor: '#fff3e0 !important',
                        borderRight: '1px solid rgba(255, 152, 0, 0.3)',
                        borderBottom: '2px solid rgba(255, 152, 0, 0.4)',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }
                    }}>
                      {renderResizableHeaderCell('month', 'Month')}
                      {renderResizableHeaderCell('status', 'Status')}
                      {renderResizableHeaderCell('media', 'Media')}
                      {renderResizableHeaderCell('sub_media', 'Sub Media')}
                      {shouldShowColumn('digital_vendor') && renderResizableHeaderCell('vendor', 'Vendor')}
                      {shouldShowColumn('digital_ad_format') && renderResizableHeaderCell('format', 'Format')}
                      {renderResizableHeaderCell('region', 'Region')}
                      {shouldShowColumn('ooh_format') && renderResizableHeaderCell('ooh', 'OOH')}
                      {shouldShowColumn('asset_video_length_image_type') && renderResizableHeaderCell('asset', 'Asset')}
                      {renderResizableHeaderCell('given', 'Given')}
                      {renderResizableHeaderCell('planning', 'Planning')}
                      {renderResizableHeaderCell('actual', 'Actual')}
                      {renderResizableHeaderCell('net_cost', 'Net Cost')}
                      {renderResizableHeaderCell('tracking', 'Tracking')}
                      {renderResizableHeaderCell('tax', 'Tax')}
                      {renderResizableHeaderCell('coupon', 'Coupon')}
                      {renderResizableHeaderCell('ooscope', 'OOScope')}
                      {renderResizableHeaderCell('saving', 'Saving')}
                      {renderResizableHeaderCell('po', 'PO')}
                      {renderResizableHeaderCell('unused', 'Unused')}
                      {renderResizableHeaderCell('ntw', 'NTW')}
                      {renderResizableHeaderCell('north', 'North')}
                      {renderResizableHeaderCell('central', 'Central')}
                      {renderResizableHeaderCell('rest', 'Rest')}
                      {renderResizableHeaderCell('ghcm', 'GHCM')}
                      {renderResizableHeaderCell('mekong', 'Mekong')}
                      {renderResizableHeaderCell('check', 'Check')}
                    </TableRow>
                  </TableHead>
                  
                  <TableBody>
                    {accrualData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={20} align="center">
                          <Typography color="textSecondary" sx={{ py: 4 }}>
                            No snapshot data available for this campaign and media type
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      accrualData.map((row, rowIndex) => (
                        <TableRow 
                          key={row.id || rowIndex} 
                          hover
                          sx={{ 
                            backgroundColor: rowIndex % 2 === 0 ? '#fefefe' : '#f9f9f9',
                            '&:hover': { 
                              backgroundColor: '#fff3e0',
                              '& .MuiTableCell-root': {
                                backgroundColor: 'transparent'
                              }
                            },
                            '& .MuiTableCell-root': {
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                              borderBottom: '1px solid rgba(224, 224, 224, 0.3)'
                            }
                          }}
                        >
                          {renderTableBodyCell('month', row.month ? format(new Date(row.month), 'MMM yyyy') : '-')}
                          {renderTableBodyCell('status', row.is_locked ? '🔒 Locked' : '🔓 Unlocked')}
                          {renderTableBodyCell('media', row.media_type || '-')}
                          {renderTableBodyCell('sub_media', cleanSubMediaTypeForDisplay(row.sub_media_type) || '-')}
                          {shouldShowColumn('digital_vendor') && renderTableBodyCell('vendor', row.digital_vendor || '-')}
                          {shouldShowColumn('digital_ad_format') && renderTableBodyCell('format', row.digital_ad_format || '-')}
                          {renderTableBodyCell('region', row.region_focus || '-')}
                          {shouldShowColumn('ooh_format') && renderTableBodyCell('ooh', row.ooh_format || '-')}
                          {shouldShowColumn('asset_video_length_image_type') && renderTableBodyCell('asset', row.asset_video_length_image_type || '-')}
                          {renderTableBodyCell('given', formatCurrency(row.given) || '-', 'right')}
                          {renderTableBodyCell('planning', formatCurrency(row.planning_gross_cost) || '-', 'right')}
                          {renderTableBodyCell('actual', 
                            <Box sx={{ 
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              fontWeight: 500,
                              color: '#666'
                            }}>
                              {formatCurrency(row.actual_gross_cost) || '-'}
                            </Box>
                          , 'right')}
                          {renderTableBodyCell('net_cost', formatCurrency(row.net_net_cost) || '-', 'right')}
                          {renderTableBodyCell('tracking', formatCurrency(row.tracking_fees) || '-', 'right')}
                          {renderTableBodyCell('tax', formatCurrency(row.whtax) || '-', 'right')}
                          {renderTableBodyCell('coupon', formatCurrency(row.coupon) || '-', 'right')}
                          {renderTableBodyCell('ooscope', formatCurrency(row.ooscope_fees) || '-', 'right')}
                          {renderTableBodyCell('saving', 
                            <Box sx={{ 
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              padding: '4px 8px',
                              backgroundColor: '#f0f8ff',
                              borderRadius: '4px',
                              fontWeight: 500,
                              color: '#1976d2',
                              gap: '2px'
                            }}>
                              <Box>{formatCurrency(row.saving) || '-'}</Box>
                              <Box sx={{ 
                                fontSize: '0.7rem',
                                ...row.savingPercentage?.style
                              }}>
                                {row.savingPercentage?.display || '-'}
                              </Box>
                            </Box>
                          , 'right')}
                          {renderTableBodyCell('po', row.po_number || '-')}
                          {renderTableBodyCell('unused', 
                            <Box sx={{ 
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#fff8e1',
                              borderRadius: '4px',
                              fontWeight: 500,
                              color: '#f57c00'
                            }}>
                              {formatCurrency(row.unused) || '-'}
                            </Box>
                          , 'right')}
                          {renderTableBodyCell('ntw', formatCurrency(row.ntw) || '-', 'right')}
                          {renderTableBodyCell('north', formatCurrency(row.north) || '-', 'right')}
                          {renderTableBodyCell('central', formatCurrency(row.central_mid) || '-', 'right')}
                          {renderTableBodyCell('rest', formatCurrency(row.the_rest_of_central) || '-', 'right')}
                          {renderTableBodyCell('ghcm', formatCurrency(row.ghcm) || '-', 'right')}
                          {renderTableBodyCell('mekong', formatCurrency(row.mekong) || '-', 'right')}
                          {renderTableBodyCell('check', 
                            <Box sx={{ 
                              padding: '4px 8px',
                              ...row.check?.style,
                              fontWeight: 500,
                              fontSize: FONT_SIZES.body,
                              textAlign: 'center'
                            }}>
                              {row.check?.display || '-'}
                            </Box>
                          , 'center')}
                        </TableRow>
                      ))
                    )}
                    
                    {/* Totals Row */}
                    {accrualData.length > 0 && (
                      <TableRow sx={{ 
                        backgroundColor: '#fff3e0',
                        borderTop: '3px solid #ff9800',
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 10,
                        '& .MuiTableCell-root': {
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: '#e65100',
                          backgroundColor: '#fff3e0 !important',
                          borderRight: '1px solid rgba(255, 152, 0, 0.3)',
                          borderTop: '3px solid #ff9800',
                          textTransform: 'uppercase'
                        }
                      }}>
                        {renderTableBodyCell('month', 
                          <Chip 
                            label="📊 TOTAL" 
                            size="small" 
                            sx={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {renderTableBodyCell('status', '')}
                        {renderTableBodyCell('media', '')}
                        {renderTableBodyCell('sub_media', '')}
                        {shouldShowColumn('digital_vendor') && renderTableBodyCell('vendor', '')}
                        {shouldShowColumn('digital_ad_format') && renderTableBodyCell('format', '')}
                        {renderTableBodyCell('region', '')}
                        {shouldShowColumn('ooh_format') && renderTableBodyCell('ooh', '')}
                        {shouldShowColumn('asset_video_length_image_type') && renderTableBodyCell('asset', '')}
                        {renderTableBodyCell('given', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.given) || 0), 0)), 'right')}
                        {renderTableBodyCell('planning', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.planning_gross_cost) || 0), 0)), 'right')}
                        {renderTableBodyCell('actual', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.actual_gross_cost) || 0), 0)), 'right')}
                        {renderTableBodyCell('net_cost', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.net_net_cost) || 0), 0)), 'right')}
                        {renderTableBodyCell('tracking', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.tracking_fees) || 0), 0)), 'right')}
                        {renderTableBodyCell('tax', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.whtax) || 0), 0)), 'right')}
                        {renderTableBodyCell('coupon', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.coupon) || 0), 0)), 'right')}
                        {renderTableBodyCell('ooscope', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ooscope_fees) || 0), 0)), 'right')}
                        {renderTableBodyCell('saving', 
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <Box>{formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.saving) || 0), 0))}</Box>
                            <Box sx={{ fontSize: '0.7rem', color: '#666' }}>
                              {(() => {
                                const totalPlanning = accrualData.reduce((sum, row) => sum + (Number(row.planning_gross_cost) || 0), 0);
                                const totalSaving = accrualData.reduce((sum, row) => sum + (Number(row.saving) || 0), 0);
                                const totalPercentage = totalPlanning > 0 ? (totalSaving / totalPlanning) * 100 : 0;
                                return `${totalPercentage.toFixed(1)}%`;
                              })()}
                            </Box>
                          </Box>
                        , 'right')}
                        {renderTableBodyCell('po', '')}
                        {renderTableBodyCell('unused', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.unused) || 0), 0)), 'right')}
                        {renderTableBodyCell('ntw', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ntw) || 0), 0)), 'right')}
                        {renderTableBodyCell('north', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.north) || 0), 0)), 'right')}
                        {renderTableBodyCell('central', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.central_mid) || 0), 0)), 'right')}
                        {renderTableBodyCell('rest', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.the_rest_of_central) || 0), 0)), 'right')}
                        {renderTableBodyCell('ghcm', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.ghcm) || 0), 0)), 'right')}
                        {renderTableBodyCell('mekong', formatCurrency(accrualData.reduce((sum, row) => sum + (Number(row.mekong) || 0), 0)), 'right')}
                        {renderTableBodyCell('check', '', 'center')}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </TableContainer>
              </div>
            </Box>
          </>
        )}

        {/* Campaign Selector Modal */}
        <Dialog 
          open={showCampaignSelector} 
          onClose={() => setShowCampaignSelector(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6">
                Campaign không tồn tại trong Snapshot tháng {snapshotMonth}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Campaign hiện tại không có trong snapshot. Vui lòng chọn một campaign khác:
            </Typography>
            
            <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List>
                {availableCampaigns.slice(0, 10).map((campaign) => (
                  <ListItem 
                    key={campaign.id}
                    button
                    onClick={() => {
                      const newUrl = `/excel-view/${campaign.id}/${mediaType}?snapshotMonth=${snapshotMonth}`;
                      navigate(newUrl);
                      setShowCampaignSelector(false);
                    }}
                    sx={{ 
                      border: '1px solid #e0e0e0',
                      mb: 1,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {campaign.campaign_name}
                          </Typography>
                          <Chip 
                            label={campaign.brand_name || 'No Brand'} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            ID: {campaign.id} | Budget: {formatCurrency(campaign.given_lc || 0)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCampaignSelector(false)}>
              Đóng
            </Button>
            <Button 
              variant="contained"
              onClick={() => navigate('/campaigns')}
              startIcon={<ArrowBackIcon />}
            >
              Về trang Campaigns
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        </Box>
      </Container>
      </Box>
    </ExcelViewWrapper>
  );
};

export default CampaignsSnapshotExcel; 